export interface ACMEResponse<T> {
  success: boolean;
  payload: T;
}

export type SortDirection = 'asc' | 'desc';

export interface Pagination {
  count: number;
  page: number;
  pageSize: number;
  sortDirection: SortDirection;
  sortField: string;
}

export interface Paginator {
  page: number;
  pageSize: number;
  sortDirection: SortDirection;
  sortField: string;
}

export function createPaginator(props: Partial<Paginator> = {}): Paginator {
  const paginator: Paginator = {
    page: 1,
    pageSize: -1,
    sortDirection: 'asc',
    sortField: 'name',
  };

  if (props) {
    paginator.page = props.page || 1;
    paginator.pageSize = props.pageSize || -1;
    paginator.sortDirection = props.sortDirection || 'asc';
    paginator.sortField = props.sortField || 'name';
  }

  return paginator;
}

export interface ACMEListResponse<T> {
  success: boolean;
  payload: {
    list: T[];
    pagination: Pagination;
  };
}

export interface ACMEError {
  errorCode: number;
  message: string;
}

export interface SimpleACMEErrorResponse {
  type: string;
}

export interface WrappedSimpleACMEErrorResponse {
  success: false;
  status: string;
  code: string;
  error: string;
  errorData: SimpleACMEErrorResponse;
}

export interface ACMEErrorResponse {
  type: string;
  requestId: string;
  /**
   * Will be set to -1 if there are multiple errors.
   * Please use the `errors` property if this is the case.
   */
  errorCode: number;
  /**
   * Will contain a stringified array when there are multiple errors.
   * Please use the `errors` property if this is the case.
   */
  message: string;
  /**
   * Present when there are multiple errors.
   */
  errors?: ACMEError[];
}

export interface WrappedACMEErrorResponse {
  success: false;
  status: string;
  code: number;
  error: string;
  errorData: ACMEErrorResponse;
}

export function isSimpleACMEErrorResponse(value: unknown): value is SimpleACMEErrorResponse {
  const error = (value as SimpleACMEErrorResponse) ?? {};

  return !isACMEErrorResponse(value) && typeof error.type === 'string';
}

export function isACMEErrorResponse(value: unknown): value is ACMEErrorResponse {
  const error = (value as ACMEErrorResponse) ?? {};

  return typeof error.type === 'string' && typeof error.message === 'string';
}

/**
 * Extract the `errorData` object from any ACME error shape — wrapped or
 * unwrapped, full or simple — returning `undefined` when the value is not an
 * ACME error. Generic so callers can type the domain-specific fields their
 * endpoint adds to `errorData` (e.g. a redirect target).
 */
export function extractACMEErrorData<T extends SimpleACMEErrorResponse = ACMEErrorResponse>(
  error: unknown,
): T | undefined {
  if (isWrappedACMEErrorResponse(error) || isWrappedSimpleACMEErrorResponse(error)) {
    return error.errorData as T;
  }

  if (isACMEErrorResponse(error) || isSimpleACMEErrorResponse(error)) {
    return error as unknown as T;
  }

  return undefined;
}

export function extractACMEErrorType(error: unknown): string {
  if (isWrappedACMEErrorResponse(error) || isWrappedSimpleACMEErrorResponse(error)) {
    return error.errorData.type;
  }

  if (isACMEErrorResponse(error) || isSimpleACMEErrorResponse(error)) {
    return error.type;
  }

  return '';
}

export function extractACMEErrorMessage(error: unknown): string {
  if (isWrappedACMEErrorResponse(error) || isWrappedSimpleACMEErrorResponse(error)) {
    return error.error;
  }

  if (isACMEErrorResponse(error)) {
    return error.message;
  }

  return '';
}

export function isWrappedACMEErrorResponse(error: unknown): error is WrappedACMEErrorResponse {
  return isACMEErrorResponse((error as WrappedACMEErrorResponse)?.errorData);
}

export function isWrappedSimpleACMEErrorResponse(error: unknown): error is WrappedSimpleACMEErrorResponse {
  return isSimpleACMEErrorResponse((error as WrappedSimpleACMEErrorResponse)?.errorData);
}

export function extractACMEPayload<T>() {
  return (httpResponse: { data: ACMEResponse<T> }) => httpResponse.data.payload;
}

export function extractUnwrappedACMEError(httpResponse: { data: WrappedACMEErrorResponse }) {
  if (httpResponse?.data?.errorData) {
    return httpResponse.data.errorData;
  }

  throw httpResponse;
}

export function throwUnwrappedACMEError() {
  return (httpResponse: { data: WrappedACMEErrorResponse }) => {
    throw httpResponse.data.errorData;
  };
}
